import { BusinessUnit, CbdStructureEntry, DeviceGroupDto } from "../types/types";
import { BASE_URL } from "../utils/apiCallsUtils";

async function getCbdStructureCodes(countryCode: string): Promise<Set<string>> {
  const url = `${BASE_URL}/planning/cbd-structures`;
  try {
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      console.error("Error fetching CBD structures");
      return new Set();
    }
    const data: CbdStructureEntry[] = await response.json();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Set(
      (data || [])
        .filter((entry) => {
          if (entry.cluCodeParent !== countryCode) return false;
          if (entry.deleteDate) return false;
          if (entry.validTo) {
            const validToDate = new Date(entry.validTo);
            if (!isNaN(validToDate.getTime()) && validToDate < today) return false;
          }
          return true;
        })
        .map((entry) => entry.cluCodeChild)
    );
  } catch (error) {
    console.error("Error fetching CBD structures:", error);
    return new Set();
  }
}

export async function getStoreCodes(countryCode: string): Promise<BusinessUnit[]> {
  const [allStores, storeCodes] = await Promise.all([
    getAllStoreCodes(),
    getCbdStructureCodes(countryCode),
  ]);
  return allStores.filter((store) => storeCodes.has(store.buCode));
}

export async function resolveDeviceGroups(groupIds: string[]): Promise<DeviceGroupDto | null> {
  const params = new URLSearchParams();
  groupIds.forEach((groupId) => params.append("groupIds", groupId));
  const url = `${BASE_URL}/planning/device-group/resolve?${params.toString()}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      console.error("Error resolving device group");
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error("Error resolving device group:", error);
    return null;
  }
}

export async function getAllStoreCodes(): Promise<BusinessUnit[]> {
  const url = `${BASE_URL}/planning/cbd-business-units`;
  try {
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      console.error("Error fetching all store codes");
      return [];
    }
    const data = await response.json();
    const today = new Date().toISOString().split("T")[0];
    return (data || [])
      .filter((unit: { deleteDate?: string | null; validTo?: string | null }) => {
        if (unit.deleteDate) return false;
        if (unit.validTo && unit.validTo < today) return false;
        return true;
      })
      .map((unit: { cluCode: string; clutType: string; name: string }) => ({
        buCode: unit.cluCode,
        buType: unit.clutType,
        displayName: unit.name,
      }));
  } catch (error) {
    console.error("Error fetching all store codes:", error);
    return [];
  }
}
